import {inject, Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, map, mergeMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, from, of} from 'rxjs';

import * as CoreSharedActions from '../../core/store-shared/shared.actions';
import * as HeartbeatActions from '../../core/store-heartbeat/heartbeat.actions';
import * as NotificationActions from '../../notifications/store/notification.actions';
import * as SettingActions from '../../settings-new/store/setting.actions';
import * as UserActions from './user.actions';
import * as UserSelectors from './user.selectors';
import * as UserAccessActions from '../user-access-store/user-access.actions';
import * as UserStoreActions from '../user-stores-store/user-store.actions';
import * as UserStoreSelectors from '../user-stores-store/user-store.selectors';

import {ILoginStepProgress, ILoginSteps, IUser, LoginProgress} from '../models';
import {NavigationPageEnum} from '../models/navigations.enums';

import {LocalStorageKeysEnum} from '../../core/models';

import {path_user_access_userId, path_users_userId} from '../../../shared/shared-services/database-paths';
import {calculatePercentile} from '../../../shared/shared-utils/number/number.utils';

import {InactivityService} from '../../../shared-utilities/services-old/inactivity.service';
import {LocalStorageService} from '../../core/service';
import {UserService} from '../service/user-service/user.service';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import {AuthPersistenceType} from '../models/auth-persistence.enum';
import * as StoreSettingsActions from '../../settings/store/settings.actions';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {createUserPaths} from '../../../shared/shared-services/database-paths.user';
import {logConsoleError} from '../../../shared/shared-module-ui/utils/logger/logger';
import {SharedModalUtils} from '../../../shared/shared-utils/shared-modal/shared-modal.utils';

@Injectable()
export class UserEffects {

  private readonly store = inject(Store);

  /**
   * User id whose login already finished in this page session.
   * A later auth-state emission must not reload stores and move the selected store (FE-248).
   */
  private completedLoginUserId: string | null = null;

  //#region [User | User Profile] Update User Profile
  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserProfileDetails.updateUserProfile),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, logErrors]) => {
        const {data} = action;
        const path = createUserPaths().setUserId(data.id).user();
        const {id: _omit, ...payload} = data;
        return this.userService.updateDocumentAsObservable(path, payload, true).pipe(
          map(() => UserActions.updateUserProfileDetails.updateUserProfileSuccess({data})),
          catchError((error) => {
            const globalError: IGlobalError = {
              ...error,
              action: JSON.stringify(action),
              actionType: action.type,
              originalError: error,
              userId: data?.id,
            };
            if (logErrors) {
              logConsoleError(
                'UPDATE USER PROFILE', {
                  globalError,
                });
            }
            return of(
              UserActions.updateUserProfileDetails.updateUserProfileFailure({error: globalError}),
            );
          }),
        );
      }),
    ),
  );
  //#endregion

  //#region EFFECT setAngularFireAuthPersistence$
  setAngularFireAuthPersistence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.setAngularFireAuthPersistence),
      switchMap((action) => {
        return this.userService.setAuthPersistence(action.persistence).pipe(
          switchMap(() => {
            this.localStorageService.setLocalStorageItem<{ persistence: AuthPersistenceType }>(
              LocalStorageKeysEnum.authPersistence,
              {persistence: action.persistence},
            );
            return from([
              UserActions.setPropertyOnUserState({
                property: 'afaPersistence',
                value: action.persistence,
              }),
            ]);
          }),
          catchError((error: Error) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                },
              }),
              UserActions.setPropertyOnUserState({
                property: 'afaPersistence',
                value: 'session',
              }),
            ]),
          ),
        );
      }),
    ),
  );

  //#endregion

  //#region EFFECT loginUser$
  loginUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loginUser),
      withLatestFrom(
        this.store.select(UserSelectors.selectIsUserLoggedIn),
      ),
      switchMap(([action, isUserLoggedIn]) => {
        if (!isUserLoggedIn) {
          return from(this.userService.logIn(action.userEmail)).pipe(
            switchMap((user: IUser | null) => {
              if (user) {
                this.inactivityService.startWatching();
                return from([
                  UserActions.loginUserSuccess({user, isManualLogin: action.isManualLogin}),
                  UserActions.setPropertyOnUserState({property: 'isUserLoading', value: false}),
                ]);
              } else {
                const globalError: IGlobalError = {
                  name: 'AuthenticationError',
                  message: 'Login failed: invalid login credentials.',
                  action: JSON.stringify(action),
                  actionType: action.type,
                };
                return from([
                  NotificationActions.setGlobalErrorOccurrence({error: globalError}),
                  UserActions.setPropertyOnUserState({property: 'isUserLoading', value: false}),
                  UserActions.setPropertyOnUserState({property: 'isUserLoggedIn', value: false}),
                  UserActions.setPropertyOnUserState({
                    property: 'loginProgress',
                    value: {
                      error: globalError,
                      progress: {currentStep: 1, totalSteps: 2, percentComplete: 99} as ILoginStepProgress,
                      ['1']: {
                        sequence: 1,
                        description: 'Invalid Login Credentials',
                        completed: false,
                      },
                    } as ILoginSteps,
                  }),
                ]);
              }
            }),
            catchError((error: IGlobalError | Error) => {
              const isNetworkError: boolean = error.name === ErrorName.NetworkError;
              const globalError: IGlobalError = {
                name: error.name || ErrorName.whyGlobalError,
                message: error.message || 'Login failed.',
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: ('originalError' in error && error.originalError) ? error.originalError : error,
              } as IGlobalError;
              return from([
                NotificationActions.setGlobalErrorOccurrence({error: globalError}),
                UserActions.setPropertyOnUserState({property: 'isUserLoading', value: false}),
                UserActions.setPropertyOnUserState({property: 'isUserLoggedIn', value: false}),
                UserActions.setPropertyOnUserState({
                  property: 'loginProgress',
                  value: {
                    error: globalError,
                    progress: {currentStep: 1, totalSteps: 2, percentComplete: 99} as ILoginStepProgress,
                    ['1']: {
                      sequence: 1,
                      description: isNetworkError ? 'No Internet Connection' : 'Invalid Login Credentials',
                      completed: false,
                    },
                  } as ILoginSteps,
                }),
              ]);
            }),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  //#endregion

  //#region EFFECT loginUserSuccess$
  loginUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loginUserSuccess),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectIsUserLoggedIn)),
        this.store.pipe(select(UserSelectors.selectUser)),
        this.store.pipe(select(UserSelectors.selectLoginProgressProperty)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      ),
      switchMap(([action, isUserLoggedIn, user, loginProgress, selectedStore]) => {
        if (!isUserLoggedIn) return EMPTY;
        const sessionAlreadyReady: boolean =
          this.completedLoginUserId === action.user.id
          && (loginProgress?.percentComplete ?? 0) >= 100
          && !!selectedStore?.storeId;
        if (sessionAlreadyReady) {
          return EMPTY;
        }
        this.completedLoginUserId = action.user.id;
        const dateIso = new Date().toString();
        this.localStorageService.setLocalStorageItem<{ date: string }>(
          LocalStorageKeysEnum.authLastLogin,
          {date: dateIso},
        );
        return [
          CoreSharedActions.getVersion(),
          HeartbeatActions.startHeartbeat(),
          UserActions.getUserDetailsByUserId({userId: user.id, isLoggingIn: true}),
          UserActions.setLoginProgress({
            progress: {
              ...LoginProgress.progress,
              currentStep: 1,
              percentComplete: calculatePercentile(1, LoginProgress.progress.totalSteps),
            },
          }),
        ];
      }),
    ),
  );
  //#endregion

  //#region EFFECT logOutUser$
  logOutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logOutUser),
      tap(() => {
        this.completedLoginUserId = null;
      }),
      withLatestFrom(this.store.select(UserSelectors.selectIsUserLoggedIn)),
      switchMap(([action, isUserLoggedIn]) => {
        if (isUserLoggedIn) return EMPTY;
        return from(this.userService.logOut()).pipe(
          switchMap(() => [
            UserActions.logOutUserSuccess(),
          ]),
          catchError((error: Error) =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                },
              }),
              UserStoreActions.setNavigation({
                feature: NavigationPageEnum.Login,
                navigation: {
                  currentPage: NavigationPageEnum.Login,
                  currentTab: null,
                },
              }),
            ),
          ),
        );
      }),
    ),
  );
  //#endregion

  //#region EFFECT getUserDetailsByUserId$
  getUserDetailsByUserId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserDetailsByUserId),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectIsUserLoggedIn)),
        this.store.pipe(select(UserSelectors.selectLoginProgressProperty)),
      ),
      switchMap(([action, isUserLoggedIn, loginSteps]) => {
        if (!isUserLoggedIn) return EMPTY;
        return this.userService.getDocumentAsObservable<IUser>(
          path_users_userId(action.userId),
        ).pipe(
          switchMap((user: IUser) => {
            const coreActions = [
              UserActions.getUserDetailsByUserIdSuccess({user}),
              UserAccessActions.getUserAccess({user, isLoggingIn: action.isLoggingIn}),
            ];
            const maybeProgressAction = action.isLoggingIn
              ? [
                UserActions.setLoginProgress({
                  progress: {
                    ...loginSteps,
                    currentStep: 2,
                    percentComplete: calculatePercentile(2, loginSteps.totalSteps),
                  },
                }),
              ]
              : [];
            return from([...coreActions, ...maybeProgressAction]);
          }),
          catchError((error: IGlobalError) =>
            of(
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  message: 'Error getting user details.',
                  originalError: error,
                },
              }),
              UserActions.setPropertyOnUserState({
                property: 'isUserLoading',
                value: false,
              }),
            ),
          ),
        );
      }),
    ),
  );

  //#endregion

  //#region EFFECT resetUserPassword$
  resetUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resetUserPassword),
      withLatestFrom(
        this.store.select(UserSelectors.selectUser),
        this.store.select(UserStoreSelectors.selectSelectedStore),
      ),
      exhaustMap(([action, user, store]) => {
        return from(this.userService.resetUserPassword(action.userEmail, user?.id, store?.storeId)).pipe(
          mergeMap(() => {
            const modalProps: ISharedModalBasic = {
              modalTitle: 'Reset Password Email Sent',
              modalTitleIcon: IconsEnum.infoSlabCircleOutline,
              modalTitleIconColor: IconColourEnum.yellow,
              contextHeading: 'Please check your email',
              buttonCloseText: 'Close',
              buttonAccept: true,
              buttonClose: false,
              buttonCloseCross: false,
              buttonAcceptText: 'Continue',
              contextDescription: ['A password reset email has been sent to your email address.'],
              cssClass: ['reset-password-modal'],
            };
            return from(this.modalUtils.createModal(modalProps)).pipe(
              map(() => UserActions.resetUserPasswordSuccess()),
            );
          }),
          catchError((error: IGlobalError) =>
            from([
              NotificationActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  message: 'Error resetting user password.',
                  originalError: error,
                },
              }),
              UserActions.resetUserPasswordFailure(),
            ]),
          ),
        );
      }),
    ),
  );

  //#endregion

  //#region EFFECT changeUserPassword$
  changeUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeUserPassword.changePassword),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, logErrors]) => {
        const {currentPassword, newPassword} = action;
        return from(this.userService.changeUserPassword(currentPassword, newPassword)).pipe(
          switchMap(async () => {
            const modalProps: ISharedModalBasic = {
              modalTitle: 'Password Changed Successfully',
              modalTitleIcon: IconsEnum.checkAll,
              modalTitleIconColor: IconColourEnum.green,
              contextHeading: 'Your password has been updated',
              buttonCloseText: 'Close',
              buttonAccept: true,
              buttonClose: false,
              buttonCloseCross: false,
              buttonAcceptText: 'Continue',
              contextDescription: ['Your password has been changed successfully.'],
            };
            await this.modalUtils.createModal(modalProps);
            return UserActions.changeUserPassword.changePasswordSuccess();
          }),
          catchError((error: IGlobalError) => {
            const globalError: IGlobalError = {
              ...error,
              action: JSON.stringify(action),
              actionType: action.type,
              originalError: error,
            };
            if (logErrors) {
              logConsoleError(
                'CHANGE USER PASSWORD', {
                  globalError,
                });
            }
            return of(
              UserActions.changeUserPassword.changePasswordFailure({error: globalError}),
            );
          }),
        );
      }),
    ),
  );

  //#endregion

  //#region EFFECT changeUserPasswordFailure$
  changeUserPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeUserPassword.changePasswordFailure),
      map((action) => {
        return NotificationActions.setGlobalErrorOccurrence({error: action.error});
      }),
    ),
  );

  //#endregion

  deleteUserAccessAsAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.deleteUserAccessAsAdmin),
      withLatestFrom(this.store.pipe(select(UserSelectors.selectIsUserLoggedIn))),
      switchMap(([action, isUserLoggedIn]) => {
        if (!isUserLoggedIn) {
          return of(
            NotificationActions.setGlobalErrorOccurrence({
              error: {
                message: 'Not logged in',
                action: JSON.stringify(action),
                actionType: action.type,
              } as IGlobalError,
            }),
          );
        }

        return this.userService
          .deleteUserAccess(path_user_access_userId(action.userId))
          .pipe(
            map(() => {
                const modalProps: ISharedModalBasic = {
                  buttonAccept: false,
                  buttonAcceptText: '',
                  buttonClose: true,
                  buttonCloseCross: false,
                  buttonCloseText: 'Confirm',
                  modalTitle: 'User Removed',
                  modalTitleIcon: IconsEnum.checkAll,
                  modalTitleIconColor: IconColourEnum.green,
                  contextHeading: `User ${action.userId} has been removed`,
                  contextDescription: [],
                };
                return StoreSettingsActions.showModal({modalProps});
              },
            ),
            catchError((error: IGlobalError) => {
                return of(
                  NotificationActions.setGlobalErrorOccurrence({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      message: 'Error Removing User Access',
                      originalError: error,
                    },
                  }),
                );
              },
            ),
          );
      }),
    ),
  );


  //#endregion

  constructor(
    private actions$: Actions,
    private inactivityService: InactivityService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private modalUtils: SharedModalUtils,
  ) {
  }
}
